import React, { useCallback, useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import PropTypes from 'prop-types';
import styled from 'styled-components';

//import theme from '@ecp/common/src/style/theme/default';
import { overrideProperties } from '@ecp/common/src/style/utils';
import { positionPropTypes, sizePropTypes } from '@ecp/common/src/style/propTypes';
import { FlexBox } from '@ecp/common/src/layouts/flex/styled';
import FavoriteCheckBox from '@ecp/common/src/components/checkbox/FavoriteCheckBox';
import Badge from '@ecp/common/src/components/badge/Badge';
import { BADGE_TYPE } from '@ecp/common/src/const/good';
import { getIsLogin } from '@fo-recoil/common/user/selector';

import BgDelete from '@fo-assets/image/common/button/btn_image_delete.svg';

import CartIcon from '@ecp/common/src/assets/icon/icon_good-card-cart.svg';

const FavoriteDecorator = ({ badgeContent: valueProp, onChange, ...badgeProps }) => {
  const [value, setValue] = useState(valueProp);
  const login = useRecoilValue(getIsLogin);

  useEffect(() => {
    setValue(valueProp);
  }, [valueProp]);

  const handleChange = useCallback(
    (e) => {
      e.stopPropagation();
      
        onChange?.(e.target.checked);
        if (login) setValue(e.target.checked);
    },
    [onChange, setValue]
  );

  const randomComponentId = (id) => {
    return id.concat(Math.floor(Math.random() * (9999 - 1) + 1));
  };

  return (
    <DecoratorRoot {...badgeProps}>
      <FavoriteCheckBox
        id={randomComponentId('wish')}
        checked={value}
        onChange={handleChange}
        title={badgeProps.title}
      />
    </DecoratorRoot>
  );
};
FavoriteDecorator.propTypes = {
  badgeContent: PropTypes.bool,
  onChange: PropTypes.func,
  title: PropTypes.string,
};

const DeleteButton = styled.button`
  position: absolute;
  right: 4px;
  top: 4px;
  display: inline-block;
  width: 18px;
  height: 18px;
  background: url(${BgDelete}) no-repeat 50%;
  font-size: 0;
  line-height: 0;
  cursor: pointer;
`;

const DeleteDecorator = ({ onClick, ...badgeProps }) => {
  const handleClick = () => {
    onClick();
  };
  return (
    <DecoratorRoot {...badgeProps}>
      <DeleteButton onClick={handleClick} />
    </DecoratorRoot>
  );
};
DeleteDecorator.propTypes = {
  badgeContent: PropTypes.string,
  onClick: PropTypes.func.isRequired,
};

const CartButton = styled.button`
  position: absolute;
  display: inline-block;
  width: ${(props) => props.size};
  height: ${(props) => props.size};
  background: url(${CartIcon}) no-repeat 50%;
  background-size: ${(props) => `${props.size} ${props.size}`};
  cursor: pointer;
`;

const CartDecorator = ({ onChange, cardType, ...badgeProps }) => {
  const handleClick = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      onChange();
    },
    [onChange]
  );
  
  const randomComponentId = (id) => {
    return id.concat(Math.floor(Math.random() * (9999 - 1) + 1));
  };
  
  const size = cardType === 'gridSmall'? '30px':'40px';
  
  return (
    <DecoratorRoot {...badgeProps}>
      <CartButton id={randomComponentId('cart')} onClick={handleClick} size={size}/>
    </DecoratorRoot>
  );
};
CartDecorator.propTypes = {
  onChange: PropTypes.func,
  cardType: PropTypes.string,
};

const GoodBadge = React.forwardRef(({ type, cardType, imageWidth: size, rootProps, ...props }, ref) => {
  let sizeProps = {};
  let positionProps = {};
  let otherProps = {};
  let badgeComponent;

  switch (type) {
    case BADGE_TYPE.LARGE:
      sizeProps = { width: '50px', height: '50px' };
      positionProps = { top: 0, left: 0 };
      badgeComponent = LargeDecorator;
      break;
    case BADGE_TYPE.PERCENT:
      sizeProps = { width: '50px', height: '50px' };
      positionProps = { top: 0, left: 0 };
      badgeComponent = PercentDecorator;
      otherProps = { max: 100 };
      break;
    case BADGE_TYPE.SMALL:
      sizeProps = { width: '30px', height: '30px' };
      positionProps = { top: 0, left: 0 };
      badgeComponent = SmallDecorator;
      break;
    case BADGE_TYPE.CART:
      sizeProps = { width: '28px', height: '28px' };
      positionProps = cardType === 'gridSmall' ? { bottom: '4px', right: '4px' } : { bottom: '10px', right: '10px' };
      badgeComponent = CartDecorator;
      break;
    case BADGE_TYPE.FAVORITE:
      sizeProps = { width: '20px', height: '20px' };
      positionProps = cardType === 'gridSmall' ? { bottom: '8px', right: '8px' } : { bottom: '10px', right: '10px' };
      badgeComponent = FavoriteDecorator;
      break;
    case BADGE_TYPE.DELETE:
      sizeProps = { width: '18px', height: '18px' };
      positionProps = { top: '4px', right: '4px' };
      badgeComponent = DeleteDecorator;
      break;
    case BADGE_TYPE.BOTTOM:
      sizeProps = { width: '98px', height: '30px' };
      positionProps = { bottom: '1px', left: '1px' };
      badgeComponent = BottomDecorator;
      break;
    case BADGE_TYPE.RECENT:
      sizeProps = { width: '26px', height: '18px' };
      positionProps = { top: 0, right: 0 };
      badgeComponent = RecentDecorator;
      break;
  }

  const newProps = {
    ...otherProps,
    badgeComponent,
    size: sizeProps,
    position: positionProps,
  };
  return <Badge cardType={cardType} {...props} rootProps={{ width: size, height: size, ...rootProps }} {...newProps} ref={ref} />;
});

GoodBadge.displayName = 'GoodBadge';
GoodBadge.propTypes = {
  type: PropTypes.string,
  cardType: PropTypes.string,
  imageWidth: PropTypes.string,
  badgeContent: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.bool]),
  rootProps: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  onClick: PropTypes.func,
  onChange: PropTypes.func,
};

const DefaultBadgeRoot = styled.div`
  position: relative;
  margin: 0;
  padding: 0;
  ${overrideProperties(sizePropTypes, {})};
`;

GoodBadge.defaultProps = {
  rootComponent: DefaultBadgeRoot,
  cardType: '',
};

export default GoodBadge;

const DecoratorRoot = styled(FlexBox)`
  position: absolute;
  justify-content: center;
  ${overrideProperties(positionPropTypes, {})};
  ${overrideProperties(sizePropTypes, {})};
`;

const CornerDecorator = styled(DecoratorRoot)`
  border-radius: 8px 0 8px;
  color: ${(props) => props.theme.color.text.white};
  font-size: ${(props) => props.theme.font.size.table};
  font-weight: ${(props) => props.theme.font.weight.bold};
`;

const LargeDecorator = styled(CornerDecorator)`
  background: ${({ badgeContent }) =>
    typeof badgeContent === 'number' && badgeContent > 3
      ? (props) => props.theme.color.third
      : (props) => props.theme.color.primary};
`;

const SmallDecorator = styled(CornerDecorator)`
  background: ${({ badgeContent }) =>
    typeof badgeContent === 'number' && badgeContent > 3
      ? (props) => props.theme.color.third
      : (props) => props.theme.color.primary};
`;

const PercentDecorator = styled(CornerDecorator)`
  background: ${(props) => props.theme.color.primary};
  &::after {
    content: '%';
    font-size: ${(props) => props.theme.font.size.error};
    font-weight: ${(props) => props.theme.font.weight.medium};
    padding-top: 3px;
  }
`;

const RecentDecorator = styled(DecoratorRoot)`
  padding: 3px 1px 0 0;
  border-radius: 0 8px;
  background: ${(props) => props.theme.color.background.basic};
  color: ${(props) => props.theme.color.text.basic};
  border: 1px solid ${(props) => props.theme.color.line.basic};
  font-size: ${(props) => props.theme.font.size.error};
  font-weight: ${(props) => props.theme.font.weight.medium};
`;

const BottomDecorator = styled(CornerDecorator)`
  border-radius: 0 0 8px 8px;
  background: ${(props) => props.theme.color.third};
  opacity: 0.5;
  font-style: normal;
  font-weight: ${(props) => props.theme.font.weight.medium};
  font-size: ${(props) => props.theme.font.size.error};
  line-height: ${(props) => props.theme.font.lineHeight.demiLight};
  text-align: right;
  color: ${(props) => props.theme.color.text.white};
`;
